import { DISPLAY_STATUS } from '@constants'
import { type ChildOrder, type OrderItem } from '@hooks-api/types/dataModel/fpOrder'
import { type TrackingLabelDetails } from '@hooks-api/types/dataModel/shipments-details'

export interface OrderItemGroup {
  groupNo: number
  orderItems: OrderItem[]
  trackingLabels: TrackingLabelDetails[]
}

/** 依 tracking_no 去重。
 * 以 Map 建構特性去重：相同 tracking_no 後出現者會覆蓋先前者，故保留「最後一筆」記錄；
 * 同一 tracking_no 的各品項託運單內容視為等價，保留最後一筆即可。 */
function dedupeTrackingLabelsByTrackingNo(trackingLabels: TrackingLabelDetails[]): TrackingLabelDetails[] {
  return Array.from(
    new Map(trackingLabels.map(trackingLabel => [trackingLabel.tracking_no, trackingLabel])).values(),
  )
}

/** 將子訂單的出貨項目依 group_no 分組，群組內合併並依 tracking_no 去重 tracking_labels；
 * group_no 為 null／undefined 的品項（運費／費用／優惠券等虛擬品項，或尚未分組的品項）視為未分組品項，
 * 供訂單詳情頁逐筆顯示出貨群組使用。 */
export function buildProcessedChildOrder(
  childOrder: ChildOrder,
): { orderItemGroups: OrderItemGroup[], ungroupedOrderItems: OrderItem[] } {
  const { order_items: orderItems } = childOrder

  // group_no 型別為 Nullable<number>，故 null 與 undefined 皆視為未分組品項。
  const ungroupedOrderItems = orderItems.filter(
    orderItem => orderItem.group_no === null || orderItem.group_no === undefined,
  )

  const itemsByGroupNo = orderItems
    .filter((orderItem): orderItem is OrderItem & { group_no: number } => orderItem.group_no !== null && orderItem.group_no !== undefined)
    .reduce<Map<number, OrderItem[]>>(
      (accumulator, orderItem) => {
        const groupNo = orderItem.group_no
        return accumulator.set(groupNo, [...(accumulator.get(groupNo) ?? []), orderItem])
      },
      new Map(),
    )

  const orderItemGroups = Array.from(itemsByGroupNo.entries())
    .sort(([groupNoA], [groupNoB]) => groupNoA - groupNoB)
    // 僅保留含 product 品項的群組，避免渲染空的出貨區塊，並讓 countDisplayGroups 與實際顯示一致。
    .filter(([, groupedOrderItems]) => groupedOrderItems.some(orderItem => orderItem.order_item_type === 'product'))
    .map(([groupNo, groupedOrderItems]) => ({
      groupNo,
      orderItems: groupedOrderItems,
      trackingLabels: dedupeTrackingLabelsByTrackingNo(groupedOrderItems.flatMap(orderItem => orderItem.tracking_labels ?? [])),
    }))

  return { orderItemGroups, ungroupedOrderItems }
}

export interface TrackingLabelGroup {
  tracking_status: string | null
  tracking_status_name: string | null
  trackingLabels: TrackingLabelDetails[]
}

export interface TrackingLabelProviderGroup {
  provider: string
  provider_name: string
  statusGroups: TrackingLabelGroup[]
}

export interface ProcessedChildOrderWithIndex {
  order: ChildOrder
  orderItemGroups: OrderItemGroup[]
  ungroupedOrderItems: OrderItem[]
  startIndex: number
}

/** 將所有子訂單批次處理並附加連續出貨群組序號，確保跨子訂單的出貨群組編號不重置。 */
export function buildProcessedChildOrdersWithIndex(childOrders: ChildOrder[]): ProcessedChildOrderWithIndex[] {
  const processedOrders = childOrders.map(order => ({
    order,
    ...buildProcessedChildOrder(order),
  }))

  return processedOrders.map((processedOrder, orderIndex) => ({
    ...processedOrder,
    // Sum group counts of all preceding orders to get a continuous display index
    startIndex: processedOrders.slice(0, orderIndex).reduce((sum, previous) => sum + previous.orderItemGroups.length, 1),
  }))
}

export function isAllChildOrdersPendingPayment(childOrders: ChildOrder[]): boolean {
  return childOrders.every(childOrder => childOrder.display_status === DISPLAY_STATUS.PENDING_PAYMENT)
}

/** 若所有子訂單皆為待付款狀態，直接將各子訂單的全部品項視為未分組品項返回；
 * 否則執行一般出貨分組與序號累計流程。 */
export function buildProcessedChildOrdersWithIndexByDisplayStatus(childOrders: ChildOrder[]): ProcessedChildOrderWithIndex[] {
  const allPendingPayment = isAllChildOrdersPendingPayment(childOrders)

  if (allPendingPayment) {
    return childOrders.map(order => ({
      order,
      orderItemGroups: [],
      ungroupedOrderItems: order.order_items,
      startIndex: 1,
    }))
  }

  return buildProcessedChildOrdersWithIndex(childOrders)
}

/** 計算所有出貨顯示區塊的總數，用於判斷是否需要顯示序號（僅有多個區塊時才顯示）。
 * 每個 orderItemGroup 對應一個 ShipmentOrder 區塊；
 * 每個子訂單中有未分組 product 品項時，額外計一個 NoShipmentOrderItems 區塊。 */
export function countDisplayGroups(processedOrders: ProcessedChildOrderWithIndex[]): number {
  return processedOrders.reduce((total, { orderItemGroups, ungroupedOrderItems }) => {
    const hasUngroupedProducts = ungroupedOrderItems.some(item => item.order_item_type === 'product')
    return total + orderItemGroups.length + (hasUngroupedProducts ? 1 : 0)
  }, 0)
}

/** 將託運單列表先依 provider 分組，再依 tracking_status 子分組，供前端依物流商及貨態分層顯示追蹤資訊使用。 */
export function groupTrackingLabelsByStatus(trackingLabels: TrackingLabelDetails[]): TrackingLabelProviderGroup[] {
  const groupsByProvider = trackingLabels.reduce<Map<string, { provider_name: string, statusMap: Map<string | null, TrackingLabelGroup> }>>(
    (accumulator, trackingLabel) => {
      const { provider, provider_name, tracking_status, tracking_status_name } = trackingLabel
      const providerEntry = accumulator.get(provider) ?? { provider_name, statusMap: new Map() }
      const existingStatusGroup = providerEntry.statusMap.get(tracking_status)
      if (existingStatusGroup) {
        existingStatusGroup.trackingLabels.push(trackingLabel)
      }
      else {
        providerEntry.statusMap.set(tracking_status, { tracking_status, tracking_status_name, trackingLabels: [trackingLabel] })
      }
      return accumulator.set(provider, providerEntry)
    },
    new Map(),
  )

  return Array.from(groupsByProvider.entries()).map(([provider, { provider_name, statusMap }]) => ({
    provider,
    provider_name,
    statusGroups: Array.from(statusMap.values()),
  }))
}
